import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { resetStore } from '../../actions';
import { useNotification } from '../../hooks/useNotification';

const LogoutCallback: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { unsubscribeFromNotifications, unregisterServiceWorker } = useNotification();

  useEffect(() => {
    const handleLogoutCleanup = async () => {
      try {
        console.log('🚪 Starting logout cleanup...');

        // Create a timeout to ensure we don't wait forever
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Cleanup timeout')), 15000); // 15 seconds timeout
        });

        // Race between cleanup and timeout
        await Promise.race([
          (async () => {
            // Unsubscribe from notifications with individual timeout
            try {
              const unsubscribePromise = unsubscribeFromNotifications();
              const unsubscribeTimeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Unsubscribe timeout')), 8000)
              );
              await Promise.race([unsubscribePromise, unsubscribeTimeout]);
              console.log('✅ Successfully unsubscribed from notifications');
            } catch (error) {
              console.warn('⚠️ Failed to unsubscribe from notifications:', error);
            }

            // Unregister service worker with individual timeout
            try {
              const unregisterPromise = unregisterServiceWorker();
              const unregisterTimeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Unregister timeout')), 5000)
              );
              await Promise.race([unregisterPromise, unregisterTimeout]);
              console.log('✅ Successfully unregistered service worker');
            } catch (error) {
              console.warn('⚠️ Failed to unregister service worker:', error);
            }

            // Clear localStorage
            localStorage.clear();
            console.log('✅ Cleared localStorage');

            // Reset Redux store
            dispatch(resetStore());
            console.log('✅ Reset Redux store');

            // Add a longer delay to ensure all operations complete
            await new Promise((resolve) => setTimeout(resolve, 3000));
          })(),
          timeoutPromise,
        ]);

        console.log('✅ All cleanup operations completed');
      } catch (error) {
        console.error('❌ Error during logout cleanup:', error);
      } finally {
        // Always redirect after cleanup (success or failure)
        console.log('🔄 Redirecting to home page...');

        // Use replace instead of href to prevent back button issues
        // Add a small delay to ensure all operations are truly complete
        setTimeout(() => {
          console.log('🔄 Executing redirect...');
          window.location.replace('/');
        }, 1000);
      }
    };

    handleLogoutCleanup();
  }, [dispatch, unsubscribeFromNotifications, unregisterServiceWorker]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        flexDirection: 'column',
        gap: '24px',
        margin: 0,
        padding: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: '#1a365d',
        color: 'white',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          textAlign: 'center',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '600',
            color: 'white',
          }}
        >
          {t('logout.loggingOut')}
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: '16px',
            color: '#e2e8f0',
            opacity: 0.9,
          }}
        >
          {t('logout.pleaseWait')}
        </p>
        <div
          style={{
            width: '48px',
            height: '48px',
            border: '4px solid rgba(255, 255, 255, 0.2)',
            borderTop: '4px solid #4299e1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginTop: '8px',
          }}
        />
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default LogoutCallback;
